import { motion } from "framer-motion";
import { TEST_IDS } from "@/constants/testIds";
import { useT } from "@/i18n/LanguageContext";

const items = [
  {
    slug: "natural-diamonds",
    inquiryType: "Diamond Inquiry",
    image:
      "https://customer-assets.emergentagent.com/job_coumba-refined/artifacts/4mysy7is_naturaldiamonds.jpg",
    alt: "Natural loose diamonds selected for private sourcing and wholesale clients",
  },
  {
    slug: "fancy-color-diamonds",
    inquiryType: "Diamond Inquiry",
    image:
      "https://customer-assets.emergentagent.com/job_coumba-refined/artifacts/ulfm5crn_fancydiamonds.jpg",
    alt: "Natural fancy color diamonds — champagne, pink and orange loose rough stones",
  },
  {
    slug: "precious-gemstones",
    inquiryType: "Precious Stone Inquiry",
    image:
      "https://customer-assets.emergentagent.com/job_coumba-refined/artifacts/lqpveepo_aquamarine.jpg",
    alt: "Natural aquamarine rough crystals curated for their origin and quality",
  },
  {
    slug: "jewelry",
    inquiryType: "Jewelry / Custom Request",
    image:
      "https://customer-assets.emergentagent.com/job_coumba-refined/artifacts/iyjusy4q_schmuck.jpg",
    alt: "Bespoke diamond ring created around sourced natural diamonds",
  },
];

const handleInquire = (inquiryType) => {
  window.dispatchEvent(
    new CustomEvent("ck:preselect-inquiry-type", { detail: inquiryType })
  );
  const el = document.getElementById("contact");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const PreciousStones = () => {
  const { t } = useT();
  const cards = t("stones.cards");
  const inquireLabel = t("stones.inquire");

  return (
    <section
      id="offering"
      data-testid={TEST_IDS.stones.root}
      className="relative py-28 md:py-40 bg-white"
    >
      <div className="container-luxe">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-12"
        >
          <div className="lg:col-span-6">
            <div className="flex items-center gap-3 mb-6">
              <span className="font-mono text-[11px] tracking-[0.4em] text-gold">
                {t("stones.index")}
              </span>
              <div className="h-px w-12 bg-gold" />
              <span className="overline">{t("stones.overline")}</span>
            </div>
            <h2
              data-testid={TEST_IDS.stones.heading}
              className="h-display text-4xl md:text-5xl lg:text-6xl text-ink text-balance"
            >
              {t("stones.title1")}
              <br />
              <span className="italic">{t("stones.title2")}</span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:pt-4">
            <p className="text-base md:text-lg leading-relaxed text-muted_text font-light">
              {t("stones.body1")}
            </p>
            <p className="mt-5 text-base md:text-lg leading-relaxed text-muted_text font-light">
              {t("stones.body2")}
            </p>
          </div>
        </motion.div>

        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {items.map((item, i) => {
            const card = cards[i];
            return (
              <motion.article
                key={item.slug}
                data-testid={TEST_IDS.stones.card(item.slug)}
                role="button"
                tabIndex={0}
                aria-label={`${card.title} — ${inquireLabel}`}
                onClick={() => handleInquire(item.inquiryType)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleInquire(item.inquiryType);
                  }
                }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: i * 0.08 }}
                className="group relative overflow-hidden cursor-pointer h-[460px] focus:outline-none focus-visible:ring-1 focus-visible:ring-gold/60"
              >
                <div
                  role="img"
                  aria-label={item.alt}
                  className="absolute inset-0 bg-cover bg-center scale-110 group-hover:scale-100 transition-transform ease-out"
                  style={{
                    backgroundImage: `url(${item.image})`,
                    transitionDuration: "1800ms",
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/40 to-transparent transition-opacity duration-700 group-hover:from-ink/95" />
                <div className="absolute inset-0 border border-white/0 group-hover:border-white/30 transition-colors duration-700" />

                <div className="relative h-full flex flex-col justify-between p-7 lg:p-8 text-white">
                  <div className="flex items-start justify-between gap-3">
                    <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/75 leading-snug">
                      {card.top}
                    </span>
                  </div>

                  <div>
                    <h3 className="font-display text-2xl md:text-3xl text-white leading-tight">
                      {card.title}
                    </h3>
                    <div className="mt-6 pt-5 border-t border-white/15 flex items-center justify-end">
                      <span
                        data-testid={`stones-card-inquire-${item.slug}`}
                        className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-white/80 group-hover:text-gold_soft transition-colors duration-500"
                      >
                        {inquireLabel}
                        <span
                          aria-hidden
                          className="inline-block transition-transform duration-500 group-hover:translate-x-1"
                        >
                          →
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};
